"use client";

import { useCallback, useEffect } from "react";

import SharedHashHighlighter from "@workspace/gamja-platform/components/hash-highlighter";

const LANG_SUFFIX_REGEX = /-(c|java|python)$/;
const LANG_CLASSES = ["c", "java", "python"] as const;

// mdx-components.tsx의 createId와 동일한 로직
function createSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-zA-Z0-9\s-\u3131-\u3163\uac00-\ud7a3]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim()
    .replace(/^-|-$/g, "");
}

// SectionLevel 뱃지를 제외한 헤딩 텍스트 추출
function getHeadingTextWithoutSectionLevel(heading: Element): string {
  const clone = heading.cloneNode(true) as Element;
  clone
    .querySelectorAll("[data-section-level], [data-llm-copy-control]")
    .forEach((el) => el.remove());
  return clone.textContent || "";
}

// 헤딩 ID에 언어 접미사 추가
// 1. 언어 div(.c-only, .java-only, .python-only) 내부 헤딩: 해당 언어 접미사
// 2. 공통 헤딩(언어 div 바깥): 현재 선택된 언어 접미사
function patchLangHeadingIds() {
  // 1. 언어 div 내부 헤딩: 해당 언어 접미사 추가
  for (const lang of LANG_CLASSES) {
    document.querySelectorAll(`.${lang}-only`).forEach((container) => {
      container.querySelectorAll("h2[id], h3[id], h4[id]").forEach((heading) => {
        const text = getHeadingTextWithoutSectionLevel(heading);
        const baseSlug = createSlug(text);
        if (baseSlug) {
          heading.id = `${baseSlug}-${lang}`;
        }
      });
    });
  }

  // 2. 공통 헤딩: 현재 언어 접미사 추가 (언어 div가 있는 페이지에서만)
  const hasLangDivs = document.querySelector(".c-only, .java-only, .python-only");
  if (hasLangDivs) {
    const currentLang =
      document.documentElement.getAttribute("data-code-lang") || "c";
    document
      .querySelectorAll("article h2[id], article h3[id], article h4[id]")
      .forEach((heading) => {
        if (heading.closest(".c-only, .java-only, .python-only")) return;
        const text = getHeadingTextWithoutSectionLevel(heading);
        const baseSlug = createSlug(text);
        if (baseSlug) {
          heading.id = `${baseSlug}-${currentLang}`;
        }
      });
  }
}

export default function HashHighlighter() {
  // 해시 스크롤 재시도·강조 코어는 공용 패키지가 소유한다.
  // JCG 는 코드 언어(C/Java/Python) 전환이라는 앱 고유 관심사만 주입한다.
  const onBeforeResolve = useCallback((hash: string): boolean => {
    const decoded = decodeURIComponent(hash.slice(1));
    const match = decoded.match(LANG_SUFFIX_REGEX);
    if (!match) return false;

    const lang = match[1];
    const currentLang = document.documentElement.getAttribute("data-code-lang");
    if (currentLang === lang) return false;

    document.documentElement.setAttribute("data-code-lang", lang);
    // 일정 앵커로 자동 전환할 때도 평소 선호 언어는 유지한다.
    if (new URLSearchParams(window.location.search).get("studyLang") !== lang) {
      localStorage.setItem("preferred-code-lang", lang);
    }
    window.dispatchEvent(new CustomEvent("lang-switch", { detail: lang }));
    return true;
  }, []);

  // 언어 전환 시 공통 헤딩 ID 재패치 (코어 재시도와 별개로 이벤트 기반)
  useEffect(() => {
    const handleLangSwitchForPatching = () => patchLangHeadingIds();
    window.addEventListener("lang-switch", handleLangSwitchForPatching);
    return () =>
      window.removeEventListener("lang-switch", handleLangSwitchForPatching);
  }, []);

  return (
    <SharedHashHighlighter
      onBeforeResolve={onBeforeResolve}
      onResolveTick={patchLangHeadingIds}
    />
  );
}
